import React from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router';
import {List} from 'immutable';
import {callAction} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';
import {addToast} from '@albert-io/atomic';
import AttributionInfo from 'client/Dennis/QuestionSetSearch/QuestionReview/AttributionInfo/AttributionInfo.react';
import SupplementManagerModal from 'client/Supplements/SupplementManager/SupplementManagerModal.react';
import {getSubjectAuthorPermissionsQuery} from 'client/Dennis/Content/Queue/shared';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';

import QuestionEditor from './QuestionEditor/QuestionEditor.react';
import QuestionPreview from './QuestionPreview/QuestionPreview.react';
import QuestionActions from './QuestionActions/QuestionActions.react';
import QuestionSetActions from './QuestionSetActions/QuestionSetActions.react';
import questionEditorStore from './QuestionEditor/QuestionEditor.store';
import questionEditorActions from './QuestionEditor/QuestionEditor.actions';

import './edit-page.scss';

class EditPage extends React.Component {
  static propTypes = {
    params: PropTypes.shape({
      questionSetId: PropTypes.string,
      questionId: PropTypes.string
    }),
    subjectAuthorPermissions: PropTypes.instanceOf(List)
  };

  /* eslint camelcase: ["error", {allow: ["^UNSAFE_"]}] */
  UNSAFE_componentWillMount() {
    if (!questionEditorStore.hasChanges()) {
      callAction(questionEditorActions.INITIALIZE_STORE);
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (
      this.props.params.questionId !== nextProps.params.questionId ||
      this.props.params.questionSetId !== nextProps.params.questionSetId
    ) {
      callAction(questionEditorActions.INITIALIZE_STORE);
    }
    if (questionEditorStore.hasToastData()) {
      addToast({
        type: questionEditorStore.getToastType(),
        message: questionEditorStore.getToastText(),
        duration: 4000
      });

      callAction(questionEditorActions.CLEAR_TOAST_DATA);
    }
  }

  togglePreviewMode = () => {
    const isPreviewMode = questionEditorStore.isPreviewMode();
    callAction(questionEditorActions.SET_IS_PREVIEW_MODE, !isPreviewMode);
  };

  canEditQuestionSet() {
    const questionSet = questionEditorStore.getQuestionSet();
    if (sessionStore.isSuper() || !questionSet || !questionSet.existsOnServer()) {
      // Supers can always edit. If the author is making a new question set, they can edit.
      return true;
    }
    if (sessionStore.getUserId() === questionSet.getMeta().getAuthorId()) {
      // If the author is looking at their own question set, they can edit.
      return true;
    }
    // Otherwise we check to see if they can edit all questions in that subject.
    const questionSetSubjectId = questionSet.getSubjectId();
    const subjectPermissions = this.props.subjectAuthorPermissions.find(
      (perm) => perm.getAuthoringSubject().getId() === questionSetSubjectId
    );
    return subjectPermissions && subjectPermissions.isCanEditQuestions();
  }

  generateQuestion() {
    if (questionEditorStore.isPreviewMode() || !this.canEditQuestionSet()) {
      return (
        <QuestionPreview
          questionId={this.props.params.questionId}
          questionSetId={this.props.params.questionSetId}
        />
      );
    }

    return <QuestionEditor questionId={this.props.params.questionId} />;
  }

  generateAuthorInfo() {
    /**
     * Only `super` users can see author information.
     */
    if (sessionStore.isSuper() === false) {
      return null;
    }
    return (
      <div className='edit-page-author-attribution'>
        <AttributionInfo
          questionId={this.props.params.questionId}
          questionSetId={this.props.params.questionSetId}
          showNavToEditButton={false}
        />
      </div>
    );
  }

  render() {
    return (
      <div className='edit-page'>
        <div className='edit-page__actions-wrapper'>
          {this.generateAuthorInfo()}
          <QuestionSetActions
            togglePreviewMode={this.togglePreviewMode}
            isPreviewMode={questionEditorStore.isPreviewMode() || !this.canEditQuestionSet()}
            disablePreviewMode={!this.canEditQuestionSet()}
          />
          <QuestionActions />
        </div>
        <div className='edit-page__question-wrapper'>{this.generateQuestion()}</div>
        <SupplementManagerModal />
      </div>
    );
  }
}

export default withRouter(
  awaitMandarkQueries(
    () => ({
      queries: {
        subjectAuthorPermissions: getSubjectAuthorPermissionsQuery()
      }
    }),
    EditPage
  )
);
